import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons.js';
import { clients, type Client, type ClientKind } from '../data/seed.js';

const KIND_LABEL: Record<'all' | ClientKind, string> = {
  all: 'All',
  homeowner: 'Homeowners',
  business: 'Businesses',
  rental: 'Rentals',
  insurance: 'Insurance',
};

export function ClientsListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'all' | ClientKind>('all');

  const filtered: Client[] = clients.filter((c) => {
    if (kind !== 'all' && c.kind !== kind) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        c.name.toLowerCase().includes(s) ||
        (c.phone || '').toLowerCase().includes(s) ||
        (c.email || '').toLowerCase().includes(s) ||
        c.addresses.some((a) => (a.line1 + ' ' + a.city).toLowerCase().includes(s))
      );
    }
    return true;
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Clients</h1>
          <p>
            {clients.length} total · {clients.filter((c) => c.parentId).length} sub-clients
          </p>
        </div>
        <div className="page-header-actions">
          <button className="btn primary">{Icon.plus({ size: 14 })} New client</button>
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          {Icon.search({ size: 14 })}
          <input
            className="input"
            placeholder="Search name, phone, email, address…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {(Object.entries(KIND_LABEL) as [keyof typeof KIND_LABEL, string][]).map(([k, label]) => (
          <button
            key={k}
            className={'chip ' + (kind === k ? 'active' : '')}
            onClick={() => setKind(k)}
          >
            {label}
            {k === 'all' ? ` (${clients.length})` : ''}
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="table-wrap hidden-mobile">
        <table className="t">
          <thead>
            <tr>
              <th>Client</th>
              <th>Type</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Tags</th>
              <th>Since</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} onClick={() => navigate('/clients/' + c.id)}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="avatar sm" style={{ background: 'hsl(var(--muted))' }}>
                      {c.name
                        .split(' ')
                        .map((w) => w[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{c.name}</div>
                      {c.parentId && (
                        <div style={{ fontSize: 11.5, color: 'hsl(var(--muted-foreground))' }}>
                          ↳ under {clients.find((p) => p.id === c.parentId)?.name}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <span className="pill outline">{c.kind}</span>
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {c.phone}
                </td>
                <td style={{ color: 'hsl(var(--muted-foreground))', fontSize: 12.5 }}>
                  {c.addresses[0]!.line1}, {c.addresses[0]!.city}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {c.tags.slice(0, 2).map((t) => (
                      <span key={t} className="tag">
                        {t}
                      </span>
                    ))}
                    {c.tags.length > 2 && <span className="tag">+{c.tags.length - 2}</span>}
                  </div>
                </td>
                <td style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                  {new Date(c.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="card-list only-mobile">
        {filtered.map((c) => (
          <div className="list-card" key={c.id} onClick={() => navigate('/clients/' + c.id)}>
            <div className="list-card-row">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div className="avatar sm" style={{ background: 'hsl(var(--muted))' }}>
                  {c.name
                    .split(' ')
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    className="list-card-title"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {c.name}
                  </div>
                  <div className="list-card-sub mono" style={{ fontSize: 11.5 }}>
                    {c.phone}
                  </div>
                </div>
              </div>
              {Icon.chevronRight({ size: 14 })}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <span className="pill outline">{c.kind}</span>
              {c.tags.slice(0, 2).map((t) => (
                <span key={t} className="tag">
                  {t}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
